import { createSelector } from '@reduxjs/toolkit';
import { getUnixTime, parseISO } from 'date-fns';
import { isEmpty, keys, last, mergeRight, pathOr, prop, sort, uniqBy } from 'ramda';

import ooeConstants from '../constants';
import { Order } from '../order/schemas';
import { selectEditMode, selectOverrideGuestDetails, selectReorder } from '../order/selectors';
import type { FavoriteOrderDetail } from '../types/order';
import { submitWarningErrorMessages } from '../util/customerErrorMessages';
import { stripNonPOSSpecialCharacters } from '../util/utils';
import { favoriteNameMatchesList } from '../util/validate';
import { State } from './reducer';

export const selectGuest = (state: { guest: State }) => state.guest;

// Guest form selectors
export const selectGuestForm = createSelector(selectGuest, (guest) => guest.form);
export const selectGuestValues = createSelector(selectGuestForm, (form) => form.values);
export const selectGuestFormErrors = createSelector(selectGuestForm, (form) => form.errors);
export const selectCfaId = createSelector(selectGuestValues, (values) => values.cfaId);
export const selectPhoneForm = createSelector(selectGuestValues, (values) => values.phone);
export const selectPhoneFormForApi = createSelector(selectPhoneForm, (phone) => {
  if (!phone) {
    return '';
  }
  if (phone[0] === '+') {
    return `+1 ${phone.split(' ').slice(1).join('')}`;
  }
  return `+1 ${phone.split(' ').join('')}`;
});
export const selectFirstName = createSelector(selectGuestValues, (values) =>
  stripNonPOSSpecialCharacters(values.first),
);
export const selectLastName = createSelector(selectGuestValues, (values) =>
  stripNonPOSSpecialCharacters(values.last),
);
export const selectFullName = createSelector(
  selectFirstName,
  selectLastName,
  (firstName, lastName) => `${firstName} ${lastName}`,
);
export const selectEmail = createSelector(selectGuestValues, (values) => values.email);
export const selectValidEmail = createSelector(
  selectGuestFormErrors,
  (errors) => isEmpty(errors) || !errors.email,
);
export const selectValidPhone = createSelector(
  selectGuestFormErrors,
  (errors) => isEmpty(errors) || !errors.phone,
);
export const selectGuestDetailsValid = createSelector(selectGuestFormErrors, (errors) => isEmpty(errors));

export const selectGuestFormSyncErrors = createSelector(selectGuestFormErrors, (errors) => {
  function mapName(formFieldName: string) {
    if (formFieldName === 'first' || formFieldName === 'last') {
      return `${formFieldName} name`;
    }
    return formFieldName;
  }
  if (isEmpty(errors)) {
    return [];
  }
  return keys(errors).map(mapName);
});

export const selectSearchResults = createSelector(selectGuest, ({ searchResults }) =>
  uniqBy(prop('cfaId'), [
    ...searchResults.emailSearch,
    ...searchResults.phoneSearch,
    ...searchResults.nameSearch,
  ]),
);

export const selectVaultedCards = createSelector(selectGuest, (guest) => guest.vaultedCards);
export const selectCardsLoading = createSelector(selectGuest, (guest) => guest.loading.vaultedCards);
export const selectZipLoading = createSelector(selectGuest, (guest) => guest.loading.validateZip);
export const selectCardSelected = createSelector(selectGuest, (guest) => guest.selectedCard || {});
export const selectPayment = createSelector(selectCardSelected, (card) => ({
  accountId: card === 'error' || typeof card.id !== 'string' ? undefined : card.id,
  paymentType: 'ACCOUNT',
}));

export const selectEmptyCmtCustomerPhone = createSelector(selectGuest, (guest) => guest.emptyCfaPhoneField);

export const selectSearchIsLoading = createSelector(
  selectGuest,
  (guest) => guest.loading.emailSearch || guest.loading.phoneSearch || guest.loading.nameSearch,
);
export const selectIsCmtCustomerSelected = createSelector(
  selectGuest,
  (guest) => !!guest.cmtCustomer.isMember,
);
export const selectCmtCustomer = createSelector(selectGuest, (guest) => guest.cmtCustomer);
export const selectCmtCustomerCFAId = createSelector(
  selectCmtCustomer,
  (cmtCustomer) => cmtCustomer.cfaId || '',
);
export const selectCmtCustomerIsRegisteredMember = createSelector(
  selectCmtCustomer,
  (cmtCustomer) => cmtCustomer.isMember || false,
);
export const selectCmtCustomerOriginalPhone = createSelector(
  selectCmtCustomer,
  (cmtCustomer) => cmtCustomer.originalPhone || '',
);
export const selectCmtCustomerName = createSelector(
  selectGuest,
  (guest) => guest.cmtCustomer.firstName || guest.cmtCustomer.first,
);
export const selectPastOrders = createSelector(selectGuest, (guest) => {
  const orders = pathOr([], ['pastOrders'], guest);
  const compare = (a: Order, b: Order) => {
    if (typeof b.promiseDateTime !== 'string' || typeof a.promiseDateTime !== 'string') {
      return 0;
    }
    return getUnixTime(parseISO(b.promiseDateTime)) - getUnixTime(parseISO(a.promiseDateTime));
  };
  return sort(compare, orders);
});
export const selectPastOrdersLength = createSelector(selectPastOrders, (orders) => orders.length);
export const selectNoMorePastOrdersToLoad = createSelector(
  selectGuest,
  (guest) => guest.noMorePastOrdersToLoad,
);
export const selectPastAndFavoritesLoading = createSelector(
  selectGuest,
  (guest) => guest.loading.pastOrders && guest.loading.getFavoriteOrders,
);
export const selectLoadingPastOrders = createSelector(selectGuest, (guest) => guest.loading.pastOrders);
export const selectFavoriteOrders = createSelector(selectGuest, (guest) => {
  const flattenedFavorites = guest.favoriteOrders.map((favorite) =>
    mergeRight(
      favorite,
      pathOr<FavoriteOrderDetail>(
        {
          orderId: '',
          lineItems: [],
          orderType: '',
          specialInstructions: '',
        },
        ['orderDetail'],
        favorite,
      ),
    ),
  );
  return flattenedFavorites.filter((favorite) => favorite.orderType === 'catering');
});
export const selectPastAndFavoriteOrders = createSelector(
  selectPastOrders,
  selectFavoriteOrders,
  (past, favorite) => [...favorite, ...past],
);
export const selectPastOrdersError = createSelector(selectGuest, (guest) => !!guest.error.pastOrders);
export const selectGuestErrors = createSelector(selectGuest, (guest) => guest.error);
export const selectDisplayableGuestErrors = createSelector(selectGuestErrors, (guestErrors) => {
  const displayableErrors = Object.entries(guestErrors).filter(([key, _]) => {
    return key !== 'getFavoriteOrders';
  });
  return Object.fromEntries(displayableErrors);
});
export const selectFavoritesLoading = createSelector(
  selectGuest,
  (guest) =>
    guest.loading.addToFavorites || guest.loading.removeFromFavorites || guest.loading.updateFavoriteName,
);
export const selectFavoritesError = createSelector(
  selectGuest,
  (guest) => guest.error.addToFavorites || guest.error.removeFromFavorites || guest.error.updateFavoriteName,
);
export const selectFavoriteActionSuccessful = createSelector(
  selectGuest,
  (guest) => guest.favoriteActionSuccessful,
);
export const selectCmtCustomerSubmitWarning = createSelector(
  selectSearchResults,
  selectIsCmtCustomerSelected,
  (searchResults, cmtCustomerSelected) => {
    if (searchResults.length > 0 && !cmtCustomerSelected) {
      return {
        type: 'warning',
        message: submitWarningErrorMessages.CMT_CUSTOMER,
      };
    }
    return {};
  },
);
export const selectCurrentFavoriteNames = createSelector(selectFavoriteOrders, (favoriteOrders) =>
  favoriteOrders.map((order) => prop('name', order)),
);
export const selectFavoriteSuggestedName = createSelector(
  selectCurrentFavoriteNames,
  selectCmtCustomerName,
  (favoriteNames, firstName) => {
    const suggestedName = `${firstName}'s Catering Order`;
    const nameMatches = favoriteNameMatchesList(favoriteNames, suggestedName);
    // If there are matches, find the number and increment by 1
    if (nameMatches.length) {
      const newNumber = Number((last(nameMatches) || '').match(ooeConstants.FIND_NUMBER_REGEX)) + 1;
      // If the suggested number is 1, start at 2 (per requirements)
      if (newNumber === 1) {
        return `${suggestedName} 2`;
      }
      return `${suggestedName} ${newNumber}`;
    }
    if (!firstName) {
      return '';
    }
    // If no matches are found, return original suggested name
    return suggestedName;
  },
);

export const selectPastDeliveryAddresses = createSelector(
  selectGuest,
  (guest) => guest?.pastDeliveryAddresses,
);

export const selectPastDeliveryAddressesLoading = createSelector(
  selectGuest,
  (guest) => guest?.loading?.getPastDeliveryAddresses,
);

export const selectNoPastDeliveryAddresses = createSelector(
  selectGuest,
  (guest) => guest?.noPastDeliveryAddresses,
);

export const selectDisableGuestFields = createSelector(
  selectEditMode,
  selectIsCmtCustomerSelected,
  selectOverrideGuestDetails,
  (editMode, cmtCustomerSelected, override) => (override ? false : editMode || cmtCustomerSelected),
);

export const selectDisableGuestPhone = createSelector(
  selectEditMode,
  selectCmtCustomerOriginalPhone,
  selectReorder,
  selectOverrideGuestDetails,
  (editMode, phone, reorder, override) =>
    override
      ? false
      : editMode || reorder || (phone !== '+1 111 111 1111' && ooeConstants.PHONE_REGEX.test(phone)),
);

export const selectMemberSearchValue = createSelector(selectGuest, (guest) => guest.memberSearchValue);
export const selectMemberSearchResults = createSelector(selectGuest, (guest) => guest.memberSearchResults);
export const selectMemberSearchLoading = createSelector(selectGuest, (guest) => guest.loading.memberSearch);
export const selectMemberSearchLoadingMore = createSelector(
  selectGuest,
  (guest) => guest.loading.memberSearchMore,
);
export const selectMemberSearchError = createSelector(selectGuest, (guest) => guest.error.memberSearch);
export const selectMemberSearchOpen = createSelector(selectGuest, (guest) => guest.memberSearchOpen);
export const selectMemberSearchNextPage = createSelector(selectGuest, (guest) => guest.memberSearchNextPage);
